import { useCallback, useEffect, useRef, useState } from 'react';

export type UseDebouncedValueOptions = {
  /** Whether the first update to `value` should be immediate or not */
  leading?: boolean;
};

const DEFAULT_OPTIONS: UseDebouncedValueOptions = { leading: false };

/**
 * This hook allows users to debounce an incoming value by a specified number of milliseconds.
 *
 * Users can also specify whether the first update to `value` in a sequence of rapid updates should be immediate, by specifying `leading: true` in the options.
 * The default value for `leading` is `false`.
 *
 * The return value is a tuple containing the debounced value at `arr[0]` and a function to cancel the pending debounced value change at `arr[1]`.
 *
 * When `waitMs` is 0 (or negative), the value is returned directly without any
 * debounce. This avoids the `useEffect` → `setState` render lag that would
 * otherwise cause downstream consumers to see stale data for one render cycle.
 *
 * @param value - The value to debounce.
 * @param waitMs - How long in milliseconds should.
 * @param options - Optional options for configuring the debounce.
 * @returns An array tuple of `[debouncedValue, cancelFn]`.
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300);
 *
 * useSearchResources('Patient', { name: debouncedSearchTerm });
 * ```
 */
export function useDebouncedValue<T>(
  value: T,
  waitMs: number,
  options: UseDebouncedValueOptions = DEFAULT_OPTIONS,
): [T, () => void] {
  const isDisabled = waitMs <= 0;
  const [debouncedValue, setDebouncedValue] = useState(value);
  const mountedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cooldownRef = useRef(false);

  const cancel = useCallback(() => globalThis.clearTimeout(timeoutRef.current), []);

  // Keep debouncedValue in sync when debounce is disabled so that stale
  // state doesn't linger if waitMs transitions from >0 to 0 at runtime.
  useEffect(() => {
    if (isDisabled) {
      setDebouncedValue(value);
    }
  }, [isDisabled, value]);

  useEffect(() => {
    if (isDisabled || !mountedRef.current) {
      return;
    }

    if (!cooldownRef.current && options.leading) {
      cooldownRef.current = true;
      setDebouncedValue(value);
    } else {
      cancel();
      timeoutRef.current = setTimeout(() => {
        cooldownRef.current = false;
        setDebouncedValue(value);
      }, waitMs);
    }
  }, [value, options.leading, waitMs, cancel, isDisabled]);

  useEffect(() => {
    mountedRef.current = true;
    return cancel;
  }, [cancel]);

  // When debounce is disabled, return value directly — no render lag.
  if (isDisabled) {
    return [value, cancel];
  }

  return [debouncedValue, cancel] as const;
}
